<template>
  <div>
    <!-- Loading Dialog -->
    <naj-modal
      ref="loadingModal"
      :header="false"
      :width="300"
      no-gutters
      persistent
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          {{ local.common.loading }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </naj-modal>

    <!-- Error Dialog -->
    <naj-modal
      ref="errorModal"
      :header="false"
      :width="300"
      no-gutters
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          {{ local.lawsuitReport.errorTitle }}
        </v-card-title>
        <v-card-text>{{ local.lawsuitReport.errorDetails }}</v-card-text>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="green darken-1"
            text
            @click="getLawsuitCaseReport"
          >
            {{ local.common.tryAgain }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </naj-modal>

    <!-- Report -->
    <printing-page v-if="data">
      <papers-header :title="local.lawsuitPaper.title">
        <field
          :label="local.lawsuitPaper.requestNumber"
          :value="data.requestCode | hindiNumbers"
          type="h3"
        ></field>
        <field
          :label="local.lawsuitPaper.requestDate"
          :value="data.requestDate | hindiNumbers"
          type="h3"
        ></field>
        <field
          :label="local.lawsuitPaper.attachments"
          value="---"
          type="h3"
        ></field>
      </papers-header>

      <paper v-hindi-numbers>
        <div
          v-for="(party, index) in data.lawsuitReportCaseParties"
          :key="index"
        >
          <headline :title="`${local.lawsuitReport.detailsText} ${party.roleInCase}`"></headline>

          <block :title="getPartyTitle(party.partyType)">
            <row :gutter-top="0">
              <column>
                <field
                  :label="local.lawsuitPaper.name"
                  :value="party.fullName"
                ></field>
              </column>

              <column v-if="party.commercialRegisterNo">
                <field
                  :label="local.lawsuitReport.commercialRegisterNo"
                  :value="party.commercialRegisterNo"
                ></field>
              </column>

              <column v-if="party.nationality">
                <field
                  :label="local.lawsuitReport.nationality"
                  :value="party.nationality"
                ></field>
              </column>

              <column v-if="party.gender">
                <field
                  :label="local.lawsuitReport.gender"
                  :value="party.gender"
                ></field>
              </column>

              <column v-if="party.isPrisoner">
                <field
                  :label="local.lawsuitReport.isPrisoner"
                  :value="party.isPrisoner"
                ></field>
              </column>

              <column v-if="party.socialIDType">
                <field
                  :label="local.lawsuitReport.socialIDType"
                  :value="party.socialIDType"
                ></field>
              </column>

              <column v-if="party.socialID">
                <field
                  :label="local.lawsuitReport.socialID"
                  :value="party.socialID"
                ></field>
              </column>

              <column v-if="party.profession">
                <field
                  :label="local.lawsuitReport.profession"
                  :value="party.profession"
                ></field>
              </column>
            </row>
          </block>

          <block
            v-if="
              isCompanyOrOrganization(party.partyType) ||
                (isPerson(party.partyType) && isPostAddressRetrieved(party))
            "
            :title="local.lawsuitReport.postAddress"
          >
            <row :gutter-top="0">
              <column v-if="party.postAddressCityName">
                <field
                  :label="local.lawsuitReport.postAddressCityName"
                  :value="party.postAddressCityName"
                ></field>
              </column>

              <column v-if="party.postAddressDistrict">
                <field
                  :label="local.lawsuitReport.postAddressDistrict"
                  :value="party.postAddressDistrict"
                ></field>
              </column>

              <column v-if="party.postAddressStreetName">
                <field
                  :label="local.lawsuitReport.postAddressStreetName"
                  :value="party.postAddressStreetName"
                ></field>
              </column>

              <column v-if="party.postAddressBuildingNo">
                <field
                  :label="local.lawsuitReport.postAddressBuildingNo"
                  :value="party.postAddressBuildingNo"
                ></field>
              </column>

              <column v-if="party.postAddressFlatNo">
                <field
                  :label="local.lawsuitReport.postAddressFlatNo"
                  :value="party.postAddressFlatNo"
                ></field>
              </column>

              <column v-if="party.postAddressCode">
                <field
                  :label="local.lawsuitReport.postAddressCode"
                  :value="party.postAddressCode"
                ></field>
              </column>
            </row>
          </block>

          <block
            v-if="isPerson(party.partyType)"
            :title="local.lawsuitReport.workFor"
          >
            <row :gutter-top="0">
              <column v-if="party.workTypeName">
                <field
                  :label="local.lawsuitReport.workTypeName"
                  :value="party.workTypeName"
                ></field>
              </column>

              <column v-if="party.workingPlaceName">
                <field
                  :label="local.lawsuitReport.workingPlaceName"
                  :value="party.workingPlaceName"
                ></field>
              </column>
            </row>
          </block>

          <block
            v-if="!isCompanyOrOrganization(party.partyType) && isAddressRetrieved(party)"
            :title="getPartyAddressTitle(party.partyType)"
          >
            <row :gutter-top="0">
              <column v-if="party.countryName">
                <field
                  :label="local.lawsuitReport.countryName"
                  :value="party.countryName"
                ></field>
              </column>

              <column v-if="party.regionName">
                <field
                  :label="local.lawsuitReport.regionName"
                  :value="party.regionName"
                ></field>
              </column>

              <column v-if="party.cityName">
                <field
                  :label="local.lawsuitReport.cityName"
                  :value="party.cityName"
                ></field>
              </column>

              <column v-if="party.districtName">
                <field
                  :label="local.lawsuitReport.districtName"
                  :value="party.districtName"
                ></field>
              </column>

              <column v-if="party.streetName">
                <field
                  :label="local.lawsuitReport.streetName"
                  :value="party.streetName"
                ></field>
              </column>

              <column v-if="party.buildingNo">
                <field
                  :label="local.lawsuitReport.buildingNo"
                  :value="party.buildingNo"
                ></field>
              </column>
            </row>
          </block>

          <block
            v-if="isPerson(party.partyType)"
            :title="local.lawsuitReport.phones"
          >
            <row :gutter-top="0">
              <column v-if="party.mobile">
                <field
                  :label="local.lawsuitReport.mobile"
                  :value="party.mobile"
                ></field>
              </column>

              <column v-if="party.workPhone">
                <field
                  :label="local.lawsuitReport.workPhone"
                  :value="party.workPhone"
                ></field>
              </column>

              <column v-if="party.fax">
                <field
                  :label="local.lawsuitReport.fax"
                  :value="party.fax"
                ></field>
              </column>
            </row>
          </block>
        </div>

        <headline :title="local.lawsuitReport.caseDetails"></headline>

        <block>
          <row>
            <column full-width>
              <field
                :label="local.lawsuitReport.caseDemand"
                :value="data.caseDemand || '---'"
              ></field>
            </column>

            <column full-width>
              <field
                :label="local.lawsuitReport.caseProof"
                :value="data.caseProof || '---'"
              ></field>
            </column>

            <column full-width>
              <field
                :label="local.lawsuitReport.caseSubject"
                :value="data.caseSubject || '---'"
              ></field>
            </column>

            <column full-width>
              <field
                :label="local.lawsuitReport.caseType"
                :value="data.caseType || '---'"
              ></field>
            </column>

            <column full-width>
              <field
                :label="local.lawsuitReport.courtName"
                :value="data.courtName || '---'"
              ></field>
            </column>

            <column full-width>
              <field
                :label="local.lawsuitReport.requestPersonName"
                :value="data.requestPersonName || '---'"
              ></field>
            </column>
          </row>
        </block>
      </paper>

      <signature :title="local.lawsuitPaper.signature"></signature>

      <action></action>
    </printing-page>
  </div>
</template>

<script>
import PrintingPage from '@/components/printing/page.vue';
import Row from '@/components/printing/row.vue';
import Column from '@/components/printing/column.vue';
import Block from '@/components/printing/block.vue';
import Field from '@/components/printing/field.vue';
import Paper from '@/components/printing/paper.vue';
import PapersHeader from '@/components/printing/header.vue';
import Signature from '@/components/printing/signature.vue';
import Action from '@/components/printing/action.vue';
import Headline from '@/components/printing/headline.vue';
import NajModal from '@/components/shared/naj-modal.vue';
import { partyTypeEnum } from '@/helpers/enum.js';
import { createNamespacedHelpers } from 'vuex';

const { mapActions } = createNamespacedHelpers('CasesModule');

export default {
  components: {
    PrintingPage,
    Row,
    Block,
    Column,
    Field,
    Paper,
    PapersHeader,
    Signature,
    Action,
    Headline,
    NajModal,
  },
  data() {
    return { data: null };
  },
  computed: {
    caseIdentity() {
      return this.$route.params.objectKey;
    },
  },
  mounted() {
    this.getLawsuitCaseReport();
  },
  methods: {
    ...mapActions(['getLawsuitReport']),
    getLawsuitCaseReport() {
      this.data = null;

      this.$refs.loadingModal.show();
      this.$refs.errorModal.close();

      this.getLawsuitReport(this.caseIdentity)
        .then((res) => {
          this.$refs.loadingModal.close();
          this.$refs.errorModal.close();
          this.data = res.data;
        })
        .catch(() => {
          this.$refs.loadingModal.close();
          this.$refs.errorModal.show();
        });
    },
    isCompanyOrOrganization(typeId) {
      return typeId === partyTypeEnum.firm || typeId === partyTypeEnum.organization;
    },
    isAddressRetrieved(party) {
      return party.countryName || party.regionName || party.cityName;
    },
    isPostAddressRetrieved(party) {
      return party.postAddressCityName || party.postAddressDistrict || party.postAddressStreetName;
    },
    isPerson(typeId) {
      return typeId === partyTypeEnum.person;
    },
    getPartyTitle(typeId) {
      switch (typeId) {
        case partyTypeEnum.person:
          return this.local.personInformation.title;

        case partyTypeEnum.firm:
          return this.local.firmData.title;

        case partyTypeEnum.organization:
          return this.local.organizationData.title;

        case partyTypeEnum.government:
          return this.local.governmentAttorney.title;

        case partyTypeEnum.embassy:
          return this.local.embassy.title;

        case partyTypeEnum.association:
          return this.local.facility.title;

        case partyTypeEnum.humanSociety:
          return this.local.attorney.title;

        case partyTypeEnum.foreignCompany:
          return this.local.foreignCompanyData.title;

        default:
          return '';
      }
    },
    getPartyAddressTitle(typeId) {
      switch (typeId) {
        case partyTypeEnum.person:
          return this.local.titleHeader.residenceAddress;

        case partyTypeEnum.firm:
          return this.local.titleHeader.addressCompany;

        case partyTypeEnum.organization:
          return this.local.titleHeader.addressCorporation;

        case partyTypeEnum.government:
          return this.local.titleHeader.addressGovernmental;

        case partyTypeEnum.embassy:
          return this.local.titleHeader.addressEmbassy;

        case partyTypeEnum.association:
          return this.local.titleHeader.addressFacility;

        case partyTypeEnum.humanSociety:
          return this.local.titleHeader.addressHumanitarian;

        case partyTypeEnum.foreignCompany:
          return this.local.titleHeader.addressCorporation;

        default:
          return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
